/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import ActionCreator from '../action-creator';
import { Organization } from '../../shared/entities/organization';

interface UpdateOrganizationProps {
    id: string | null;
    organizationData: Organization;
}

export const ActionCreators = {
    // For requesting all orgs
    RequestOrganizations: new ActionCreator<'RequestOrganizations', any>(
        'RequestOrganizations',
    ),
    ReceivedOrganizationsSuccess: new ActionCreator<'ReceivedOrganizationsSuccess', any>(
        'ReceivedOrganizationsSuccess',
    ),
    ReceivedOrganizationsError: new ActionCreator<'ReceivedOrganizationsError', any>(
        'ReceivedOrganizationsError',
    ),
    ClearReceivedOrganizationsError: new ActionCreator<'ClearReceivedOrganizationsError', any>(
        'ClearReceivedOrganizationsError',
    ),

    // For requesting specific org
    RequestCurrentOrganization: new ActionCreator<'RequestCurrentOrganization', string>(
        'RequestCurrentOrganization',
    ),
    ReceivedCurrentOrganizationSuccess: new ActionCreator<'ReceivedCurrentOrganizationSuccess', any>(
        'ReceivedCurrentOrganizationSuccess',
    ),
    ReceivedCurrentOrganizationError: new ActionCreator<'ReceivedCurrentOrganizationError', any>(
        'ReceivedCurrentOrganizationError',
    ),
    ClearReceivedCurrentOrganizationError: new ActionCreator<'ClearReceivedCurrentOrganizationError', any>(
        'ClearReceivedCurrentOrganizationError',
    ),
    
    // For adding an org
    ProvisionOrganization: new ActionCreator<'ProvisionOrganization', Organization>(
        'ProvisionOrganization',
    ),
    OrganizationProvisioningSuccess: new ActionCreator<'OrganizationProvisioningSuccess', any>(
        'OrganizationProvisioningSuccess',
    ),
    OrganizationProvisioningError: new ActionCreator<'OrganizationProvisioningError', any>(
        'OrganizationProvisioningError',
    ),
    ClearOrganizationProvisioningError: new ActionCreator<'ClearOrganizationProvisioningError', any>(
        'ClearOrganizationProvisioningError',
    ),

    // For deleting an org
    DeleteOrganization: new ActionCreator<'DeleteOrganization', string>(
        'DeleteOrganization',
    ),
    DeleteOrganizationSuccess: new ActionCreator<'DeleteOrganizationSuccess', any>(
        'DeleteOrganizationSuccess',
    ),
    DeleteOrganizationError: new ActionCreator<'DeleteOrganizationError', any>(
        'DeleteOrganizationError',
    ),
    ClearDeleteOrganizationError: new ActionCreator<'ClearDeleteOrganizationError', any>(
        'ClearDeleteOrganizationError',
    ),


    // For updating an org
    UpdateOrganization: new ActionCreator<'UpdateOrganization', UpdateOrganizationProps>(
        'UpdateOrganization',
    ),
    UpdateOrganizationSuccess: new ActionCreator<'UpdateOrganizationSuccess', any>(
        'UpdateOrganizationSuccess',
    ),
    UpdateOrganizationError: new ActionCreator<'UpdateOrganizationError', any>(
        'UpdateOrganizationError',
    ),
    ClearUpdateOrganizationError: new ActionCreator<'ClearUpdateOrganizationError', any>(
        'ClearUpdateOrganizationError',
    ),

    // For getting terminals in org
    RequestOrganizationTerminals: new ActionCreator<'RequestOrganizationTerminals', string>(
        'RequestOrganizationTerminals',
    ),
    ReceivedOrganizationTerminalsSuccess: new ActionCreator<'ReceivedOrganizationTerminalsSuccess', any>(
        'ReceivedOrganizationTerminalsSuccess',
    ),
    ReceivedOrganizationTerminalsError: new ActionCreator<'ReceivedOrganizationTerminalsError', any>(
        'ReceivedOrganizationTerminalsError',
    ),
    ClearReceivedOrganizationTerminalsError: new ActionCreator<'ClearReceivedOrganizationTerminalsError', any>(
        'ClearReceivedOrganizationTerminalsError',
    ),
};

export type Action = typeof ActionCreators[keyof typeof ActionCreators];

export function requestOrganizations() {
    return ActionCreators.RequestOrganizations.create();
}
export function clearReceivedOrganizationsError() {
    return ActionCreators.ClearReceivedOrganizationsError.create();
}


export function requestCurrentOrganization(id: string) {
    return ActionCreators.RequestCurrentOrganization.create(id);
}
export function clearReceivedCurrentOrganizationError() {
    return ActionCreators.ClearReceivedCurrentOrganizationError.create();
}


export function provisionOrganization(terminalData: Organization) {
    return ActionCreators.ProvisionOrganization.create(terminalData);
}
export function clearOrganizationProvisioningError() {
    return ActionCreators.ClearOrganizationProvisioningError.create();
}


export function deleteOrganization(id: string) {
    return ActionCreators.DeleteOrganization.create(id);
}
export function clearDeleteOrganizationError() {
    return ActionCreators.ClearDeleteOrganizationError.create();
}


export function updateOrganization(id: string, terminalData: Organization) {
    return ActionCreators.UpdateOrganization.create({
        id: id,
        organizationData: terminalData
    });
}
export function clearUpdateOrganizationError() {
    return ActionCreators.ClearUpdateOrganizationError.create();
}


export function requestOrganizationTerminals(id: string) {
    return ActionCreators.RequestOrganizationTerminals.create(id);
}
export function clearReceivedOrganizationTerminalsError() {
    return ActionCreators.ClearReceivedOrganizationTerminalsError.create();
}